import {
  useEffect,
  useState,
} from 'react'
import {
  ActionIcon,
  Avatar,
  Box,
  Divider,
  Flex,
  Group,
  Image,
  Menu,
  Tooltip,
  UnstyledButton,
} from '@mantine/core'
import {
  ChevronLeft,
  ChevronRight,
  Menu as MenuIcon,
} from 'lucide-react'
import { useNavigate } from 'react-router-dom'

import { notifyError } from '@common/utils/notify'
import { hasPermissions } from '@common/utils/validation/authValidators'
import { CountCombobox } from '@components/ui/CountCombobox'
import {
  useAccountsQuery,
  useActiveAccounts,
  useSelectedAccounts,
} from '@data/accounts/accounts'
import { useCurrentUserQuery } from '@data/currentUser/currentUser'
import { useLogout } from '@hooks/useLogout'
import { useAccountSelection } from '@data/selection/accountSelection'
import { useAuthStore } from '@store/auth.store'
import LocationSelector from '../../main/Components/LocationSelector/LocationSelector'

type TopBarProps = {
  navbarOpened: boolean
  onToggleNavbar: () => void
}

function TopBar({ navbarOpened, onToggleNavbar }: TopBarProps) {
  const navigate = useNavigate()
  const { data: userInfo } = useCurrentUserQuery()
  const permissions = useAuthStore((state) => state.permissions)
  const logout = useLogout()

  const activeAccounts = useActiveAccounts()
  const selectedAccounts = useSelectedAccounts()
  // The selectors are driven by accounts alone. Devices and water usage are not selector
  // inputs, and gating on them held both selectors behind the slowest requests in the
  // bootstrap — water usage additionally pulled the full-fleet `/water_usage` payload on
  // every app mount purely to drive this spinner. `LocationSelector` gates itself on the
  // locations query.
  const { isLoading: isLoadingAccounts } = useAccountsQuery()
  const selectAccounts = useAccountSelection()

  const [hideSelectors, setHideSelectors] = useState(false)

  const handleMyProfileClick = () => {
    navigate('/user-profile')
  }

  const handleSubscriptionsClick = () => {
    navigate('/subscriptions')
  }

  const url = window.location.hostname
  const isUATEnvironment = url.includes('uat')
  const isTestEnvironment = url.includes('test')

  const accountOptions = activeAccounts.map((account) => ({
    value: account.id,
    label: account.accountName,
  }))
  const selectedAccountIds = selectedAccounts.map((account) => account.id)
  const accountById = new Map(activeAccounts.map((account) => [account.id, account]))

  const handleSelectedAccountIdsChange = (ids: string[]) => {
    selectAccounts(ids.filter((id) => accountById.has(id)))
  }

  const handleLogout = () => {
    navigate('/', { replace: true, state: null })
    logout()
  }

  useEffect(() => {
    if (!activeAccounts.length && !isLoadingAccounts) {
      // displayToast closed errors after 5000 ms; kept, since notifyError defaults to sticky.
      notifyError(
        'An Error occurred while trying to load user profile. Please contact our support team.',
        {
          id: 'innactive-account',
          autoClose: 5000,
        },
      )

      handleLogout()
    }
    // `isLoadingAccounts` has to be here, not just `activeAccounts`. When the accounts
    // request fails, `data` stays undefined and `useAccounts` keeps handing back the same
    // module-level empty array, so the memo behind `useActiveAccounts` never recomputes and
    // `activeAccounts` never changes identity — the effect would never re-run and this
    // toast would never fire in the one case it that it exists for.
  }, [activeAccounts, isLoadingAccounts])

  const toggleSelectors = () => {
    setHideSelectors((currentValue) => !currentValue)
  }

  const selectorToggleLabel = hideSelectors
    ? 'Show account and location selectors'
    : 'Hide account and location selectors'

  return (
    <Flex
      h={'100%'}
      direction={'column'}
    >
      <Flex
        component={'nav'}
        w={'100%'}
        bg={'rgba(135, 187, 250, 0.1)'}
        flex={'1 1 auto'}
        mih={0}
        style={{ boxShadow: '0 0.15rem 1.75rem 0 rgba(58, 59, 69, 0.15)' }}
      >
        <Group
          align={'center'}
          w={'100%'}
          h={'100%'}
          wrap={'nowrap'}
        >
          <ActionIcon
            id={'sidebarToggleTop'}
            variant={'subtle'}
            color={'gray'}
            size={'2.5rem'}
            mx={'xs'}
            onClick={onToggleNavbar}
            aria-label={'Toggle sidebar'}
            aria-expanded={navbarOpened}
          >
            <MenuIcon size={18} />
          </ActionIcon>

          {/*
            Stacks below `sm` (768px), matching the pre-migration `flex-md-row` threshold: there is
            room for both selectors side by side well below Mantine's `md`. Keep this in sync with
            the AppShell `header` height breakpoint in App.tsx — stacked selectors need the taller
            header, so the two thresholds are one decision written twice.
          */}
          <Flex
            direction={{ base: 'column', sm: 'row' }}
            w={{ base: '80%', md: '50%' }}
            align={{ base: 'flex-start', md: 'center' }}
          >
            {activeAccounts.length > 1 ? (
              <>
                {/*
                  Rendered at every width on purpose. `hideSelectors` takes effect at all
                  breakpoints, so gating this control by one would leave the state reachable
                  where the only means of clearing it is not — collapsing on a desktop and
                  then narrowing would strand both selectors hidden until a reload.
                */}
                {hasPermissions(permissions, ['ACCOUNT:ADMIN:SMARTFLOW']) && (
                  <Tooltip label={selectorToggleLabel}>
                    <ActionIcon
                      variant={'subtle'}
                      color={'gray'}
                      ms={'xs'}
                      onClick={toggleSelectors}
                      aria-label={selectorToggleLabel}
                    >
                      {hideSelectors ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
                    </ActionIcon>
                  </Tooltip>
                )}

                {!hideSelectors && (
                  <Box
                    pr={{ base: 0, md: 'md' }}
                    mb={{ base: 'xs', md: 0 }}
                    w={{ base: '80%', md: '90%' }}
                    fz={'sm'}
                  >
                    <CountCombobox
                      label={'Accounts'}
                      options={accountOptions}
                      selectedValues={selectedAccountIds}
                      onSelectedValuesChange={handleSelectedAccountIdsChange}
                      loading={activeAccounts.length === 0 || isLoadingAccounts}
                      disabled={activeAccounts.length === 0 || isLoadingAccounts}
                    />
                  </Box>
                )}
              </>
            ) : (
              <Box
                component={'h4'}
                mb={'xs'}
                w={'100%'}
              >
                {activeAccounts[0]?.accountName}
              </Box>
            )}

            {!hideSelectors && (
              <Box w={'100%'} mb={{ base: 'xs', md: 0 }}>
                {!isLoadingAccounts && <LocationSelector />}
              </Box>
            )}
          </Flex>

          <Flex
            ml={'auto'}
            direction={{ base: 'column', sm: 'row' }}
            align={{ base: 'center', sm: 'stretch' }}
            gap={{ base: 15, sm: 0 }}
          >
            {/*
              `visibleFrom` replaces two bespoke media queries from the deleted TopBar.scss: the
              logo was hidden below 950px and resized below 800px, and that resize rule was
              already dead — the later `display: none` block overrode it at equal specificity.
              The threshold is deliberately Mantine's `md` (992px) rather than 950px, so the bar
              switches on the theme scale instead of hand-tuned values. The 41px band this gives
              up is a deliberate trade; do not reintroduce a bespoke breakpoint to recover it.
            */}
            <Image
              alt={'Smartflow'}
              src={'/logo.png'}
              w={220}
              h={55}
              visibleFrom={'md'}
            />
            <Group
              gap={'md'}
              mr={'md'}
              wrap={'nowrap'}
            >
              <Divider orientation={'vertical'} visibleFrom={'sm'} />
              <Menu position={'bottom-end'}>
                <Menu.Target>
                  <UnstyledButton id={'dropdown-item-button'} aria-label={'Open user menu'}>
                    <Avatar
                      variant={'filled'}
                      color={'blue'}
                      radius={'xl'}
                      size={'md'}
                    >
                      {userInfo?.initials ?? ''}
                    </Avatar>
                  </UnstyledButton>
                </Menu.Target>
                <Menu.Dropdown>
                  <Menu.Item onClick={handleMyProfileClick}>My Profile</Menu.Item>
                  {hasPermissions(permissions, ['READ:SUBSCRIPTION']) && (
                    <Menu.Item onClick={handleSubscriptionsClick}>Subscriptions</Menu.Item>
                  )}
                  <Menu.Item onClick={handleLogout}>Log out</Menu.Item>
                </Menu.Dropdown>
              </Menu>
            </Group>
          </Flex>
        </Group>
      </Flex>

      {isTestEnvironment && (
        <Box
          w={'100%'}
          bg={'red'}
          c={'white'}
          ta={'center'}
          py={2}
        >
          TEST ENVIRONMENT
        </Box>
      )}
      {isUATEnvironment && (
        <div className="w-100 bg-danger text-white text-center">UAT ENVIRONMENT</div>
      )}
    </Flex>)
}

export default TopBar
